import { BenchmarkFilters } from 'constants.js';
import { sort, SORT_DATA_TYPES } from 'utils/sort';
import { isNumber } from 'utils/math';

export const getBenchmarkColData = (sessions, benchmarkFilter, laterality) => {
  if (!sessions) return {};

  switch (benchmarkFilter) {
    case BenchmarkFilters.LAST:
      return getPersonLastSession(sessions);
    case BenchmarkFilters.BEST:
      return getPersonBestSession(sessions, laterality);
    case BenchmarkFilters.AVERAGE:
      return getPersonAverageSession(sessions, laterality);
    default:
  }
};

const getPersonLastSession = sessions => {
  const sortedSessions = sort(sessions, {
    keys: [{ key: 'sessionDate', desc: true, dataType: SORT_DATA_TYPES.NUMBER }]
  });
  if (sortedSessions.length > 0) {
    return { ...sortedSessions[0] };
  }
};

const getPersonBestSession = (sessions, laterality) => {
  if (sessions.length > 0) {
    const bestSession = sessions.reduce(function (prevSession, currentSession) {
      if (currentSession?.best_value === 1) {
        return currentSession?.result?.[laterality]?.[0] &&
          prevSession?.result?.[laterality]?.[0] &&
          Number(prevSession.result?.[laterality]?.[0]) >
            Number(currentSession.result?.[laterality]?.[0])
          ? prevSession
          : currentSession;
      } else {
        return currentSession?.result?.[laterality]?.[0] &&
          prevSession?.result?.[laterality]?.[0] &&
          Number(prevSession.result?.[laterality]?.[0]) <
            Number(currentSession.result?.[laterality]?.[0])
          ? prevSession
          : currentSession;
      }
    });
    return { ...bestSession };
  }
};

const getPersonAverageResult = (sessions, laterality) => {
  if (!Array.isArray(sessions) || sessions.length === 0) return undefined;

  let sum = 0;
  let count = 0;

  for (const s of sessions) {
    const v = Number(s?.result?.[laterality]?.[0]);
    if (isNumber(v)) {
      sum += v;
      count += 1;
    }
  }
  if (count === 0) return undefined;

  return sum / count;
};

const getPersonAverageSession = (sessions, laterality) => {
  const average = getPersonAverageResult(sessions, laterality);
  if (average === undefined) return undefined;

  return {
    ...sessions?.[0],
    best_value: sessions?.[0]?.best_value ?? null,
    result: {
      [laterality]: [String(average)]
    }
  };
};

export const getAverageOfGroup = (rows, testId, index, laterality) => {
  const average = Object.values(rows).reduce(
    (item, currentValue) => {
      const session = currentValue[index];
      if (session) {
        const result = session?.result?.[laterality]?.[0];

        if (result) {
          item.count += 1;
          item.score += Number(result);

          if (result < item.minScore) item.minScore = Number(result);
          if (result > item.maxScore) item.maxScore = Number(result);
        }
      }
      return item;
    },
    {
      count: 0,
      percentage: 0,
      score: 0,
      minScore: Number.POSITIVE_INFINITY,
      maxScore: 0
    }
  );

  return {
    ...average,
    avg: average.score / average.count
  };
};

export const getDataRows = ({ persons, testCols, benchmarkFilter }) => {
  const rows = [];
  persons.forEach(person => {
    let row = [];
    testCols.forEach(col => {
      let personCol = {};
      if (col.id) {
        const [id, laterality] = col.id.split('_');
        const sessions = person.results[id]?.filter(s => s && s.result); // Filter out sessions without results;
        personCol = getBenchmarkColData(sessions, benchmarkFilter, laterality);
      }
      row.push(personCol);
    });
    rows[person.id] = row;
  });
  return rows;
};
